import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, Plus, Search, Flag, ExternalLink, AlertTriangle, RefreshCw } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import {
  getScheduleChanges, getScheduleReviews, cancelScheduleChange, clearScheduleReview,
  getStudents, getStudent, getStaff, getAlternatePlans,
} from "../lib/api";
import ScheduleChangeForm from "../components/students/ScheduleChangeForm";
import AlternatePlanForm from "../components/students/AlternatePlanForm";
import { ChangeDetail } from "../components/students/ScheduleChangeHistory";

const CHANGE_LABELS = {
  weekly_schedule: { label: "Weekly Schedule", cls: "bg-blue-100 text-blue-800" },
  alt_plan_added: { label: "Plan Added", cls: "bg-emerald-100 text-emerald-800" },
  alt_plan_updated: { label: "Plan Updated", cls: "bg-amber-100 text-amber-800" },
  alt_plan_ended: { label: "Plan Ended", cls: "bg-rose-100 text-rose-800" },
  initial: { label: "Initial", cls: "bg-slate-100 text-slate-700" },
};
const STATUS_LABELS = {
  upcoming: { label: "Upcoming", cls: "bg-indigo-100 text-indigo-800" },
  active: { label: "Active", cls: "bg-green-100 text-green-800" },
  superseded: { label: "Superseded", cls: "bg-gray-100 text-gray-600" },
  ended: { label: "Ended", cls: "bg-rose-100 text-rose-800" },
  cancelled: { label: "Cancelled", cls: "bg-gray-100 text-gray-500 line-through" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(String(iso).slice(0, 10) + "T00:00:00");
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const addDaysStr = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const studentDisplayName = (s) => `${s.preferred_name || s.first_name || ""} ${s.last_name || ""}`.trim();

/** Searchable student picker — type a name, tap a result. */
function StudentPicker({ students, onPick }) {
  const [q, setQ] = useState("");
  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = [...students].sort((a, b) => studentDisplayName(a).localeCompare(studentDisplayName(b)));
    if (!needle) return list.slice(0, 50);
    return list.filter((s) => studentDisplayName(s).toLowerCase().includes(needle)).slice(0, 50);
  }, [students, q]);

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search students by name…"
          className="pl-9"
          data-testid="schedule-change-student-search"
        />
      </div>
      <div className="max-h-[50vh] overflow-y-auto rounded-lg border divide-y">
        {matches.length === 0 ? (
          <p className="text-center py-6 text-sm text-slate-500">No students found.</p>
        ) : (
          matches.map((s) => (
            <button
              key={s.id}
              type="button"
              className="w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center justify-between gap-2"
              onClick={() => onPick(s)}
              data-testid={`pick-student-${s.id}`}
            >
              <span className="font-medium text-slate-800">{studentDisplayName(s)}</span>
              <span className="text-xs text-slate-400">{(s.grade || "").trim()}</span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

export default function ScheduleChangesPage() {
  const navigate = useNavigate();
  const [changes, setChanges] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [upcomingAll, setUpcomingAll] = useState([]);
  const [activePlans, setActivePlans] = useState([]);
  const [students, setStudents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  const [view, setView] = useState("changes"); // changes | flagged

  // filters
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [studentQuery, setStudentQuery] = useState("");
  const [startDate, setStartDate] = useState(addDaysStr(-30));
  const [endDate, setEndDate] = useState("");

  // new-change dialog
  const [formMode, setFormMode] = useState(null); // 'schedule' | 'alt' | null
  const [pickedStudent, setPickedStudent] = useState(null);

  // detail drawer
  const [detail, setDetail] = useState(null);

  const loadChanges = useCallback(async () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (typeFilter) params.change_type = typeFilter;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    try {
      const res = await getScheduleChanges(params);
      setChanges(res.data || []);
    } catch { toast.error("Failed to load schedule changes"); }
  }, [statusFilter, typeFilter, startDate, endDate]);

  const loadMeta = useCallback(async () => {
    const [rev, up, plans] = await Promise.all([
      getScheduleReviews().catch(() => ({ data: [] })),
      getScheduleChanges({ status: "upcoming" }).catch(() => ({ data: [] })),
      getAlternatePlans({ active_only: true }).catch(() => ({ data: [] })),
    ]);
    setReviews(rev.data || []);
    setUpcomingAll(up.data || []);
    setActivePlans(plans.data || []);
  }, []);

  useEffect(() => {
    getStudents("current", true).then((r) => setStudents(r.data || [])).catch(() => setStudents([]));
    getStaff().then((r) => setStaff(r.data || [])).catch(() => setStaff([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadChanges(), loadMeta()]).finally(() => setLoading(false));
  }, [loadChanges, loadMeta]);

  // summary chips
  const next7 = addDaysStr(7);
  const today = todayStr();
  const startingSoon = useMemo(
    () => upcomingAll.filter((c) => c.effective_date && c.effective_date >= today && c.effective_date <= next7),
    [upcomingAll, today, next7]
  );
  const endingSoon = useMemo(
    () => activePlans.filter((p) => p.end_date && p.end_date >= today && p.end_date <= next7),
    [activePlans, today, next7]
  );

  const filteredChanges = useMemo(() => {
    const needle = studentQuery.trim().toLowerCase();
    if (!needle) return changes;
    return changes.filter((c) => (c.student_name || "").toLowerCase().includes(needle));
  }, [changes, studentQuery]);

  const openNew = (mode) => { setFormMode(mode); setPickedStudent(null); };
  const closeNew = () => { setFormMode(null); setPickedStudent(null); };

  const pickStudent = async (s) => {
    try {
      const res = await getStudent(s.id);
      setPickedStudent(res.data);
    } catch {
      setPickedStudent(s);
    }
  };

  const onFormSaved = () => {
    closeNew();
    loadChanges();
    loadMeta();
  };

  const makeChangeFor = async (review) => {
    setFormMode("schedule");
    setPickedStudent(null);
    try {
      const res = await getStudent(review.student_id);
      setPickedStudent(res.data);
    } catch {
      setPickedStudent({ id: review.student_id, first_name: review.display_name });
    }
  };

  const markReviewed = async (studentId) => {
    try {
      await clearScheduleReview(studentId, null);
      toast.success("Review marked done");
      loadMeta();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear review");
    }
  };

  const cancelChange = async (changeId) => {
    if (!window.confirm("Cancel this upcoming schedule change?")) return;
    try {
      await cancelScheduleChange(changeId);
      toast.success("Upcoming change cancelled");
      setDetail(null);
      loadChanges();
      loadMeta();
    } catch { toast.error("Failed to cancel"); }
  };

  const Chip = ({ testid, icon, label, value, tone, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex-1 min-w-[150px] text-left rounded-xl border p-4 transition-colors ${tone} ${onClick ? "hover:brightness-95 cursor-pointer" : "cursor-default"}`}
    >
      <div className="flex items-center gap-2 text-xs font-medium opacity-80">{icon}{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </button>
  );

  return (
    <div className="space-y-6" data-testid="schedule-changes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#00305E] flex items-center gap-2">
            <CalendarClock className="h-6 w-6" /> Schedule Changes
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Make and track schedule &amp; alternate-plan changes for any student.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => openNew("schedule")} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="new-schedule-change-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Schedule Change
          </Button>
          <Button onClick={() => openNew("alt")} variant="outline" className="border-[#00305E] text-[#00305E]" data-testid="new-alt-plan-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Alternate Plan
          </Button>
        </div>
      </div>

      {/* Summary chips */}
      <div className="flex flex-wrap gap-3">
        <Chip testid="chip-active-plans" icon={<CalendarClock className="h-3.5 w-3.5" />} label="Active alternate plans" value={activePlans.length} tone="bg-amber-50 border-amber-200 text-amber-800" />
        <Chip testid="chip-starting-soon" icon={<CalendarClock className="h-3.5 w-3.5" />} label="Starting in next 7 days" value={startingSoon.length} tone="bg-indigo-50 border-indigo-200 text-indigo-800" onClick={() => { setView("changes"); setStatusFilter("upcoming"); }} />
        <Chip testid="chip-ending-soon" icon={<CalendarClock className="h-3.5 w-3.5" />} label="Ending in next 7 days" value={endingSoon.length} tone="bg-rose-50 border-rose-200 text-rose-800" />
        <Chip testid="chip-flagged" icon={<Flag className="h-3.5 w-3.5" />} label="Flagged for review" value={reviews.length} tone="bg-orange-50 border-orange-200 text-orange-800" onClick={() => setView("flagged")} />
      </div>

      {/* View toggle */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setView("changes")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${view === "changes" ? "border-[#00305E] text-[#00305E]" : "border-transparent text-slate-500"}`}
          data-testid="view-changes-tab"
        >All Changes</button>
        <button
          onClick={() => setView("flagged")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1.5 ${view === "flagged" ? "border-[#00305E] text-[#00305E]" : "border-transparent text-slate-500"}`}
          data-testid="view-flagged-tab"
        >
          Flagged for Review
          {reviews.length > 0 && <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-white text-[10px] font-semibold">{reviews.length}</span>}
        </button>
      </div>

      {view === "changes" ? (
        <Card>
          <CardContent className="pt-6 space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap items-end gap-3">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input value={studentQuery} onChange={(e) => setStudentQuery(e.target.value)} placeholder="Search by student…" className="pl-9" data-testid="filter-student-search" />
              </div>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 rounded-md border border-input bg-background px-2 text-sm" data-testid="filter-status">
                <option value="">All statuses</option>
                <option value="upcoming">Upcoming</option>
                <option value="active">Active</option>
                <option value="superseded">Superseded</option>
                <option value="ended">Ended</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="h-10 rounded-md border border-input bg-background px-2 text-sm" data-testid="filter-type">
                <option value="">All types</option>
                <option value="weekly_schedule">Weekly Schedule</option>
                <option value="alt_plan_added">Plan Added</option>
                <option value="alt_plan_updated">Plan Updated</option>
                <option value="alt_plan_ended">Plan Ended</option>
                <option value="initial">Initial</option>
              </select>
              <div className="flex items-center gap-1.5">
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-[150px]" data-testid="filter-start-date" />
                <span className="text-slate-400 text-sm">→</span>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-[150px]" data-testid="filter-end-date" />
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setStatusFilter(""); setTypeFilter(""); setStudentQuery(""); setStartDate(addDaysStr(-30)); setEndDate(""); }} data-testid="reset-filters-btn">Reset</Button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[820px]">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-2 pr-3 font-medium">Student</th>
                    <th className="py-2 pr-3 font-medium">Type</th>
                    <th className="py-2 pr-3 font-medium">Summary</th>
                    <th className="py-2 pr-3 font-medium">Effective</th>
                    <th className="py-2 pr-3 font-medium">End</th>
                    <th className="py-2 pr-3 font-medium">Status</th>
                    <th className="py-2 pr-3 font-medium">Reason</th>
                    <th className="py-2 pr-3 font-medium">Entered by</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [0, 1, 2, 3].map((i) => <tr key={i}><td colSpan={8} className="py-2"><div className="h-9 rounded bg-slate-100 animate-pulse" /></td></tr>)
                  ) : filteredChanges.length === 0 ? (
                    <tr><td colSpan={8} className="py-10 text-center text-slate-500" data-testid="changes-empty">No schedule changes match these filters.</td></tr>
                  ) : (
                    filteredChanges.map((c) => {
                      const tm = CHANGE_LABELS[c.change_type] || { label: c.change_type, cls: "bg-gray-100 text-gray-700" };
                      const sm = STATUS_LABELS[c.status] || { label: c.status, cls: "bg-gray-100" };
                      return (
                        <tr key={c.id} className="border-b last:border-0 hover:bg-slate-50 cursor-pointer" onClick={() => setDetail(c)} data-testid={`change-row-${c.id}`}>
                          <td className="py-2.5 pr-3">
                            <button
                              className="font-medium text-[#00305E] hover:underline text-left"
                              onClick={(e) => { e.stopPropagation(); navigate(`/students/${c.student_id}?tab=schedule`); }}
                              data-testid={`change-student-link-${c.id}`}
                            >{c.student_name || "A student"}</button>
                          </td>
                          <td className="py-2.5 pr-3"><span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${tm.cls}`}>{tm.label}</span></td>
                          <td className="py-2.5 pr-3 text-slate-700 max-w-[260px] truncate">{c.summary}</td>
                          <td className="py-2.5 pr-3 text-slate-600 whitespace-nowrap">{fmtDate(c.effective_date)}</td>
                          <td className="py-2.5 pr-3 text-slate-600 whitespace-nowrap">{c.end_date ? fmtDate(c.end_date) : "—"}</td>
                          <td className="py-2.5 pr-3"><span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${sm.cls}`}>{sm.label}</span></td>
                          <td className="py-2.5 pr-3 text-slate-600">{c.reason_category || "—"}</td>
                          <td className="py-2.5 pr-3 text-slate-600">{c.created_by_name || "—"}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-muted-foreground flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" /> Students whose schedule is flagged for review.</p>
              <Button variant="outline" size="sm" onClick={loadMeta} data-testid="refresh-flagged-btn"><RefreshCw className="h-4 w-4 mr-1.5" />Refresh</Button>
            </div>
            {reviews.length === 0 ? (
              <div className="text-center py-10 text-sm text-slate-500" data-testid="flagged-empty">No students are flagged for schedule review right now.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                  <thead>
                    <tr className="text-left text-slate-500 border-b">
                      <th className="py-2 pr-3 font-medium">Student</th>
                      <th className="py-2 pr-3 font-medium">Grade</th>
                      <th className="py-2 pr-3 font-medium">Reason</th>
                      <th className="py-2 pr-3 font-medium">Flagged by</th>
                      <th className="py-2 pr-3 font-medium">Review date</th>
                      <th className="py-2 pr-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviews.map((r) => (
                      <tr key={r.student_id} className="border-b last:border-0 hover:bg-amber-50/40" data-testid={`flagged-row-${r.student_id}`}>
                        <td className="py-2.5 pr-3">
                          <button className="font-medium text-[#00305E] hover:underline" onClick={() => navigate(`/students/${r.student_id}?tab=schedule`)}>{r.display_name}</button>
                        </td>
                        <td className="py-2.5 pr-3 text-slate-600">{r.grade || "—"}</td>
                        <td className="py-2.5 pr-3 text-slate-600">{r.note || <span className="text-slate-400">—</span>}</td>
                        <td className="py-2.5 pr-3 text-slate-600">{r.flagged_by_name || "—"}</td>
                        <td className="py-2.5 pr-3 text-slate-600 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1 text-amber-700"><CalendarClock className="h-3.5 w-3.5" />{fmtDate(r.review_date || r.flagged_at)}</span>
                        </td>
                        <td className="py-2.5 pr-3 text-right whitespace-nowrap">
                          <Button variant="outline" size="sm" className="mr-2" onClick={() => makeChangeFor(r)} data-testid={`flagged-make-change-${r.student_id}`}>Make change</Button>
                          <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => markReviewed(r.student_id)} data-testid={`flagged-mark-reviewed-${r.student_id}`}>Mark reviewed</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* New change / plan dialog */}
      <Dialog open={!!formMode} onOpenChange={(o) => { if (!o) closeNew(); }}>
        <DialogContent className={pickedStudent && formMode === "schedule" ? "max-w-2xl max-h-[90vh] overflow-y-auto" : "max-w-md max-h-[90vh] overflow-y-auto"}>
          <DialogHeader>
            <DialogTitle>
              {pickedStudent
                ? `${formMode === "schedule" ? "Schedule Change" : "Alternate Plan"} · ${studentDisplayName(pickedStudent)}`
                : `New ${formMode === "schedule" ? "Schedule Change" : "Alternate Plan"}`}
            </DialogTitle>
            {!pickedStudent && <DialogDescription>Choose the student this applies to.</DialogDescription>}
          </DialogHeader>
          {!pickedStudent ? (
            <StudentPicker students={students} onPick={pickStudent} />
          ) : formMode === "schedule" ? (
            <ScheduleChangeForm student={pickedStudent} studentId={pickedStudent.id} staff={staff} onSaved={onFormSaved} onCancel={closeNew} />
          ) : (
            <AlternatePlanForm studentId={pickedStudent.id} staff={staff} editingPlan={null} onSaved={onFormSaved} onCancel={closeNew} />
          )}
        </DialogContent>
      </Dialog>

      {/* Detail drawer */}
      <Sheet open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="change-detail-drawer">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle>{detail.student_name || "A student"}</SheetTitle>
                <SheetDescription>{(CHANGE_LABELS[detail.change_type] || {}).label || detail.change_type} · {(STATUS_LABELS[detail.status] || {}).label || detail.status}</SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-xs text-slate-500">Effective</p><p className="font-medium">{fmtDate(detail.effective_date)}</p></div>
                  <div><p className="text-xs text-slate-500">End date</p><p className="font-medium">{detail.end_date ? fmtDate(detail.end_date) : "—"}</p></div>
                  <div><p className="text-xs text-slate-500">Reason</p><p className="font-medium">{detail.reason_category || "—"}</p></div>
                  <div><p className="text-xs text-slate-500">Entered by</p><p className="font-medium">{detail.created_by_name || "—"}</p></div>
                  <div><p className="text-xs text-slate-500">Approved by</p><p className="font-medium">{detail.approved_by_name || "—"}</p></div>
                  <div><p className="text-xs text-slate-500">Parent notified</p><p className="font-medium">{detail.parent_notified ? `Yes${detail.parent_notified_date ? ` (${fmtDate(detail.parent_notified_date)})` : ""}` : "—"}</p></div>
                </div>
                {detail.reason_notes && <p className="text-xs text-slate-600">Notes: {detail.reason_notes}</p>}
                <p className="text-slate-700">{detail.summary}</p>
                <div className="rounded-lg border p-3 bg-slate-50/50">
                  <ChangeDetail change={detail} />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className="flex-1" onClick={() => { navigate(`/students/${detail.student_id}?tab=schedule`); }} data-testid="detail-open-file-btn">
                    <ExternalLink className="h-4 w-4 mr-1.5" /> Open student file
                  </Button>
                  {detail.status === "upcoming" && (
                    <Button variant="destructive" onClick={() => cancelChange(detail.id)} data-testid="detail-cancel-btn">Cancel change</Button>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
